import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { ShareNetwork, X, UserCircle } from "@phosphor-icons/react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function SharePanel({ recordType, recordId }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [userId, setUserId] = useState("");
  const key = ["shares", recordType, recordId];

  const { data: shares = [] } = useQuery({
    queryKey: key,
    queryFn: async () => (await api.get("/shares", { params: { record_type: recordType, record_id: recordId } })).data,
  });
  const { data: candidates = [] } = useQuery({
    queryKey: ["share-candidates"],
    queryFn: async () => (await api.get("/shares/candidates")).data,
  });

  const share = useMutation({
    mutationFn: async () => (await api.post("/shares", { record_type: recordType, record_id: recordId, user_id: userId })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: key }); setUserId(""); toast.success(t("perm.shared", "Access granted")); },
    onError: () => toast.error(t("actionFailed", "Action failed")),
  });
  const revoke = useMutation({
    mutationFn: async (sid) => (await api.delete(`/shares/${sid}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: key }); toast.success(t("perm.revoked", "Access revoked")); },
    onError: () => toast.error(t("actionFailed", "Action failed")),
  });

  const sharedIds = new Set(shares.map((s) => s.user_id));
  const options = candidates.filter((c) => !sharedIds.has(c.id));

  return (
    <div className="bg-white border border-slate-200 rounded-sm p-6" data-testid="share-panel">
      <div className="flex items-center gap-2 mb-1">
        <ShareNetwork size={18} className="text-[#C5A059]" />
        <h3 className="font-serif text-lg">{t("perm.sharing", "Sharing & access")}</h3>
      </div>
      <p className="text-xs text-slate-500 mb-4">{t("perm.sharingDesc", "Grant individual Team Members or Investors access to this record. Admin only.")}</p>

      <div className="flex gap-2 mb-4">
        <Select value={userId} onValueChange={setUserId}>
          <SelectTrigger className="flex-1 rounded-sm bg-white" data-testid="share-user-select">
            <SelectValue placeholder={t("perm.selectUser", "Select a user…")} />
          </SelectTrigger>
          <SelectContent className="bg-white">
            {options.map((c) => (
              <SelectItem key={c.id} value={c.id} data-testid={`share-option-${c.id}`}>
                {c.name} · {t(`perm.role.${c.role}`, c.role)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button disabled={!userId || share.isPending} onClick={() => share.mutate()} data-testid="share-grant-btn" className="rounded-sm bg-[#0B2545] hover:bg-[#1a365d] text-white">
          {t("perm.share", "Share")}
        </Button>
      </div>

      <div className="text-xs uppercase tracking-wide text-slate-400 mb-2">{t("perm.currentAccess", "Who has access")} ({shares.length})</div>
      {shares.length === 0 ? (
        <p className="text-sm text-slate-400" data-testid="share-empty">{t("perm.noShares", "Not shared with anyone yet.")}</p>
      ) : (
        <ul className="space-y-1.5">
          {shares.map((s) => (
            <li key={s.id} data-testid={`share-row-${s.user_id}`} className="flex items-center gap-2 text-sm border border-slate-100 rounded-sm px-3 py-2">
              <UserCircle size={18} className="text-slate-400" />
              <span className="flex-1 truncate">{s.user_name || s.user_email || s.user_id}</span>
              <span className="text-xs text-slate-400">{t(`perm.role.${s.user_role}`, s.user_role)}</span>
              <button onClick={() => revoke.mutate(s.id)} data-testid={`share-revoke-${s.user_id}`} className="text-slate-400 hover:text-red-600" title={t("perm.revoke", "Revoke")}>
                <X size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
